<script setup>
import { ref, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';

const scrollContainerElement = ref(null);
const isButtonVisible = ref(false);

const BUTTON_BOTTOM_OFFSET = '0.5rem';
const BUTTON_Z_INDEX = 10;
const MINIMUM_SCROLL_STEP = 200;
const PAGE_SCROLL_FACTOR = 0.9;

function findScrollableContainer() {
    const instance = getCurrentInstance();
    const rootElement = instance?.proxy?.$el || null;

    if (!rootElement) return null;

    const parentElement = rootElement.parentElement;

    if (parentElement) {
        const markedElement = parentElement.querySelector('[data-scroll-container]');

        if (markedElement) return markedElement;

        const candidates = Array.from(parentElement.querySelectorAll('*'));
        for (const element of candidates) {
            const overflowY = getComputedStyle(element).overflowY;

            if (overflowY === 'auto' || overflowY === 'scroll') return element;
        }
    }

    let element = rootElement;
    while (element && element.parentElement) {
        element = element.parentElement;
        const overflowY = getComputedStyle(element).overflowY;

        if (overflowY === 'auto' || overflowY === 'scroll') return element;
    }

    return null;
}

function updateButtonVisibility() {
    const element = scrollContainerElement.value;

    if (!element) {
        isButtonVisible.value = false;

        return;
    }

    const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 5;
    isButtonVisible.value = element.scrollHeight > element.clientHeight && !isAtBottom;
}

function scrollContainerDown() {
    const element = scrollContainerElement.value;

    if (!element) return;

    const scrollStep = Math.max(MINIMUM_SCROLL_STEP, Math.floor(element.clientHeight * PAGE_SCROLL_FACTOR));
    element.scrollBy({ top: scrollStep, behavior: 'smooth' });
}

function handleScrollEvent() {
    updateButtonVisibility();
}

function handleResizeEvent() {
    updateButtonVisibility();
}

onMounted(() => {
    scrollContainerElement.value = findScrollableContainer();

    if (scrollContainerElement.value) {
        scrollContainerElement.value.addEventListener('scroll', handleScrollEvent, { passive: true });
        window.addEventListener('resize', handleResizeEvent, { passive: true });
        updateButtonVisibility();
    }
});

onBeforeUnmount(() => {
    if (scrollContainerElement.value) scrollContainerElement.value.removeEventListener('scroll', handleScrollEvent);

    window.removeEventListener('resize', handleResizeEvent);
});
</script>

<template>
    <button
        v-show="isButtonVisible"
        type="button"
        :style="{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: BUTTON_BOTTOM_OFFSET,
            zIndex: String(BUTTON_Z_INDEX)
        }"
        class="pointer-events-auto inline-flex items-center justify-center gap-1 rounded-lg px-3 py-1 text-xxs font-medium text-gray-500 border border-gray-300 bg-white hover:text-blue-dark hover:border-blue-dark transition duration-300 shadow-sm w-1/2 max-w-xs"
        :aria-label="$t('show_more')"
        @click="scrollContainerDown"
    >
        <slot name="label">{{ $t('show_more') }}</slot>
        <slot name="icon">
            <i class="fi fi-rr-arrow-small-down text-base leading-none translate-y-[0.5px]"></i>
        </slot>
    </button>
</template>
